<script lang="ts" setup>
import type { FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import type { StyledFontAwesomeIcons } from '@shared/plugin/icons/types';
import { useApplicationRoot } from '@tools/applicationRoot';
import type { MenuProps } from 'primevue/menu';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { computed, ref, type MaybeRefOrGetter } from 'vue';
import VTooltip from '../VTooltip/VTooltip.vue';
import ItemLink from './components/ItemLink/ItemLink.vue';
import { getStylesObj, type VMenuTheme } from './VMenu.styles';

export type VMenuItem = Omit<MenuItem, 'icon'> & {
  badge?: string;
  icon?: FontAwesomeIconProps['icon'] | StyledFontAwesomeIcons;
  loading?: boolean;
  disabledReason?: MaybeRefOrGetter<string>;
};

export type VMenuProps = {
  appendTo?: MenuProps['appendTo'];
  model?: VMenuItem[];
  popup?: MenuProps['popup'];
  pt?: MenuProps['pt'];
  ptOptions?: MenuProps['ptOptions'];
  theme?: VMenuTheme;
};

const props = defineProps<VMenuProps>();

const menu = ref();

const applicationRoot = useApplicationRoot();

const componentProps = computed(() =>
  Object.fromEntries(Object.entries(props).filter(([key]) => key !== 'icon')),
);

const stylesObj = computed(() => getStylesObj(props.theme));

defineExpose({
  hide: (event: Event) => menu.value.hide(event),
  show: (event: Event) => menu.value.show(event),
  toggle: (event: Event) => menu.value.toggle(event),
  overlayVisible: () => menu.value.overlayVisible,
});
</script>

<template>
  <Menu
    v-bind="componentProps"
    ref="menu"
    data-testid="menu"
    :append-to="appendTo ?? applicationRoot"
  >
    <template #start>
      <slot name="start"></slot>
    </template>

    <template #item="{ item, props: menuRouterBindProps }">
      <slot name="item" :item="item" :props="menuRouterBindProps" :styles="stylesObj">
        <VTooltip v-if="item.disabled && item.disabledReason" :label="item.disabledReason">
          <ItemLink :menu-item="item" :menu-router-bind-props="menuRouterBindProps" />
        </VTooltip>

        <ItemLink v-else :menu-item="item" :menu-router-bind-props="menuRouterBindProps" />
      </slot>
    </template>
  </Menu>
</template>
